import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FilterSeparator } from '../../filters/FilterSeparator';
import { AnalyticsFilter } from './AnalyticsFilter';
import { CountryIcon, SalesChannelIcon } from '../../icons/analytics';
import { AnalyticsCurrencyFilter } from './AnalyticsCurrencyFilter';
import { AnalyticsDateFilter } from './AnalyticsDateFilter';
import {
  doesSupplierSupportMultipleCurrencies,
  getRelevantRetailersForOrderTypeFilter,
  getSalesChannelsOptions,
} from './utils';
import { supplierRetailerUtils } from '../../../utils/supplierRetailer';
import { FunnelIcon } from '../../icons/analytics/FunnelIcon';
import { ORDER_TYPE_OPTIONS, orderTypeOptions } from '../../../constants/analytics';
import { useShouldDisplayOrderTypeFilter } from './hooks/useShouldDisplayOrderTypeFilter';
import { AnalyticsOrderTypeFilter } from './AnalyticsOrderTypeFilter';

import {
  ANALYTICS_SET_CURRENCY,
  ANALYTICS_SET_FILTER,
  ANALYTICS_SET_ORDER_TYPE,
} from '../../../store/reducers/analytics';

import styles from './Dashboard.module.scss';

export function DashboardFilters() {
  const dispatch = useDispatch();
  let {
    currentCurrency,
    currentOrderType,
    countries,
    retailers,
    filters: { retailerIds, countries: selectedCountries, orderTypes: selectedOrderTypes },
  } = useSelector((state) => state.analytics);
  const { shouldDisplayOrderTypeFilter, retailerIdByOrderType } = useShouldDisplayOrderTypeFilter();
  const { settings: supplierSettings, supplier_retailers: supplierRetailers } = useSelector(
    (state) => state.role.supplier,
  );
  const [shouldDisableOrderTypeFilter, setShouldDisableOrderTypeFilter] = useState(false);
  const showCurrenciesToggler = doesSupplierSupportMultipleCurrencies(supplierSettings);

  useEffect(() => {
    if (!showCurrenciesToggler && supplierSettings?.analytics?.supported_currencies?.length) {
      dispatch({
        type: ANALYTICS_SET_CURRENCY,
        payload: { currency: supplierSettings.analytics.supported_currencies[0] },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierSettings]);

  if (retailerIds.length === 0) {
    retailerIds = supplierRetailerUtils.setRetailersActiveStatus(retailers).map((retailer) => retailer.id);
  }

  const salesChannelsOptions = useMemo(() => {
    const relevantRetailers = getRelevantRetailersForOrderTypeFilter(
      retailers,
      selectedOrderTypes,
      retailerIdByOrderType,
    );
    return getSalesChannelsOptions(supplierRetailers, retailers, relevantRetailers);
  }, [selectedOrderTypes, retailers, supplierRetailers, retailerIdByOrderType]);

  const countriesOptions = useMemo(() => {
    if (!countries) return [];

    return Object.entries(countries)
      .map(([countryCode, name]) => ({
        value: countryCode,
        label: `${name} (${countryCode})`,
      }))
      .sort((countryA, countryB) => countryA.label.localeCompare(countryB.label));
  }, [countries]);

  const areAllRetailerOptionsWithSingleOrderType = useCallback(
    (retailerIdsWithOrderType) => {
      return (
        retailerIds.every((retailerId) => retailerIdsWithOrderType.includes(retailerId)) &&
        retailerIds.length < retailerIdsWithOrderType.length
      );
    },
    [retailerIds],
  );

  useEffect(() => {
    if (!retailerIdByOrderType) setShouldDisableOrderTypeFilter(false);
    else if (Object.values(retailerIdByOrderType).find((ids) => areAllRetailerOptionsWithSingleOrderType(ids))) {
      setShouldDisableOrderTypeFilter(true);
    } else setShouldDisableOrderTypeFilter(false);
  }, [areAllRetailerOptionsWithSingleOrderType, retailerIdByOrderType, selectedOrderTypes]);

  useEffect(() => {
    if (!shouldDisplayOrderTypeFilter) return;
    dispatch({ type: ANALYTICS_SET_FILTER, payload: { orderTypes: ORDER_TYPE_OPTIONS } });
  }, [dispatch, shouldDisplayOrderTypeFilter]);

  const onChangeOrderType = (orderTypes) => {
    let retailerIdsToUpdate = retailers.map(({ id }) => id);
    if (orderTypes.length === 1) {
      retailerIdsToUpdate = retailerIdsToUpdate.filter((retailerId) =>
        retailerIdByOrderType[orderTypes].includes(retailerId),
      );
    }
    const payload = {
      orderTypes,
      retailerIds: retailerIdsToUpdate,
    };
    dispatch({ type: ANALYTICS_SET_FILTER, payload });
  };

  return (
    <div className={styles.dashboardFilters}>
      <AnalyticsDateFilter
        showToday={false}
        onChange={(startDate, endDate) =>
          dispatch({
            type: ANALYTICS_SET_FILTER,
            payload: { dates: { start: new Date(startDate), end: new Date(endDate) } },
          })
        }
      />

      <FilterSeparator />

      <AnalyticsFilter
        title="Country"
        icon={<CountryIcon />}
        filterKey="countries"
        options={countriesOptions}
        onChange={(value) => {
          dispatch({ type: ANALYTICS_SET_FILTER, payload: { countries: value } });
        }}
        selected={selectedCountries}
        withSearch={countriesOptions.length > 2}
      />

      {shouldDisplayOrderTypeFilter && (
        <AnalyticsFilter
          title="Type"
          icon={<FunnelIcon />}
          filterKey="orderTypes"
          options={orderTypeOptions}
          onChange={onChangeOrderType}
          selected={selectedOrderTypes}
          withSearch={false}
          disabled={shouldDisableOrderTypeFilter}
        />
      )}

      <AnalyticsFilter
        title="Sales channels"
        icon={<SalesChannelIcon />}
        onChange={(value) => {
          dispatch({ type: ANALYTICS_SET_FILTER, payload: { retailerIds: value } });
        }}
        options={salesChannelsOptions}
        selected={retailerIds}
        withSearch={salesChannelsOptions.length > 2}
      />

      <FilterSeparator />

      <AnalyticsOrderTypeFilter
        selected={currentOrderType}
        onChange={(value) => dispatch({ type: ANALYTICS_SET_ORDER_TYPE, payload: { orderType: value } })}
      />

      {showCurrenciesToggler && (
        <>
          <FilterSeparator />

          <AnalyticsCurrencyFilter
            selected={currentCurrency}
            onChange={(value) => dispatch({ type: ANALYTICS_SET_CURRENCY, payload: { currency: value } })}
          />
        </>
      )}
    </div>
  );
}
